import { cn } from '../lib/cn';
import { applyGuard, type GuardMode } from './sanitize';

/**
 * The shared validated inputs.
 *
 * <p><b>The markup is copied from the screens, not invented.</b> `variant='float'` reproduces the
 * panel's floating-label field (PartyForm, NewInvoice, Menu, …) and `variant='stack'` reproduces
 * the customer web app's label-above field (CustomerInfoModal, Login, Booking) — the same classes,
 * paddings and type scale. The only additions are the error border and the message line beneath,
 * which is the styling `DateField` already established. Nothing about the layout changes.</p>
 */

/** The one error line, so every field across both apps reports a problem identically. */
export function FieldError({ error, className }: { error?: string; className?: string }) {
  if (!error) return null;
  return (
    <div role='alert' className={cn('mt-1 text-[11px] font-medium text-danger', className)}>
      {error}
    </div>
  );
}

type Variant = 'float' | 'stack';

export interface ValidatedFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  type?: string;
  /** Input shaping applied on change — and therefore on paste too. */
  guard?: GuardMode;
  maxLength?: number;
  placeholder?: string;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel' | 'email' | 'url' | 'search';
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  className?: string;
  variant?: Variant;
  /** Small helper text shown under the field when there is no error. */
  hint?: string;
}

export function ValidatedField({
  label,
  value,
  onChange,
  onBlur,
  error,
  required,
  type = 'text',
  guard,
  maxLength,
  placeholder,
  inputMode,
  disabled,
  readOnly,
  autoFocus,
  className,
  variant = 'float',
  hint
}: ValidatedFieldProps) {
  const handle = (raw: string) => onChange(guard ? applyGuard(raw, guard, maxLength) : raw);

  if (variant === 'stack') {
    return (
      <div className={className}>
        <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
          {label}
          {required && <span className='text-danger'> *</span>}
        </div>
        <input
          type={type}
          value={value}
          onChange={(e) => handle(e.target.value)}
          onBlur={onBlur}
          placeholder={placeholder}
          inputMode={inputMode}
          maxLength={maxLength}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          aria-label={label}
          aria-invalid={error ? true : undefined}
          className={cn(
            'w-full rounded-xl border-[1.5px] px-4 py-3 text-sm font-medium focus:outline-none',
            error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary',
            disabled && 'opacity-60'
          )}
        />
        <FieldError error={error} />
        {!error && hint && <div className='mt-1 text-[11px] text-content-muted'>{hint}</div>}
      </div>
    );
  }

  // `float`: the label sits inside the box and rises on focus/fill. A date input never matches
  // :placeholder-shown, so its label is floated permanently.
  const alwaysFloat = type === 'date' || type === 'time' || Boolean(placeholder);
  return (
    <div className={cn('relative', className)}>
      <input
        type={type}
        value={value}
        onChange={(e) => handle(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder ?? ' '}
        inputMode={inputMode}
        maxLength={maxLength}
        disabled={disabled}
        readOnly={readOnly}
        autoFocus={autoFocus}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        className={cn(
          'peer w-full rounded-xl border bg-surface-card px-3.5 pb-[6px] pt-[18px] text-[13px] text-content-primary focus:outline-none',
          error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary',
          disabled && 'opacity-60'
        )}
      />
      <label
        className={cn(
          'pointer-events-none absolute left-3.5 text-content-muted transition-all',
          alwaysFloat
            ? 'top-[6px] text-[10px] font-semibold'
            : 'top-[15px] text-[13px] peer-focus:top-[6px] peer-focus:text-[10px] peer-focus:font-semibold peer-focus:text-primary peer-[:not(:placeholder-shown)]:top-[6px] peer-[:not(:placeholder-shown)]:text-[10px] peer-[:not(:placeholder-shown)]:font-semibold'
        )}
      >
        {label}
        {required && <span className='text-danger'>*</span>}
      </label>
      <FieldError error={error} />
      {!error && hint && <div className='mt-1 text-[11px] text-content-muted'>{hint}</div>}
    </div>
  );
}

export interface ValidatedSelectProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  options: readonly (string | { value: string; label: string })[];
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  variant?: Variant;
}

export function ValidatedSelect({
  label,
  value,
  onChange,
  onBlur,
  options,
  error,
  required,
  disabled,
  placeholder = 'Select…',
  className,
  variant = 'float'
}: ValidatedSelectProps) {
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));

  const select = (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      disabled={disabled}
      aria-label={label}
      aria-invalid={error ? true : undefined}
      className={cn(
        variant === 'stack'
          ? 'w-full appearance-none rounded-xl border-[1.5px] px-4 py-3 text-sm font-medium focus:outline-none'
          : 'w-full appearance-none rounded-xl border bg-surface-card px-3.5 pb-[6px] pt-[18px] text-[13px] focus:outline-none',
        error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary',
        value ? 'text-content-primary' : 'text-content-muted',
        disabled && 'opacity-60'
      )}
    >
      <option value=''>{placeholder}</option>
      {opts.map((o) => (
        <option key={o.value} value={o.value} className='text-content-primary'>
          {o.label}
        </option>
      ))}
    </select>
  );

  if (variant === 'stack') {
    return (
      <div className={className}>
        <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
          {label}
          {required && <span className='text-danger'> *</span>}
        </div>
        {select}
        <FieldError error={error} />
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {select}
      <label className='pointer-events-none absolute left-3.5 top-[6px] text-[10px] font-semibold text-content-muted'>
        {label}
        {required && <span className='text-danger'>*</span>}
      </label>
      <FieldError error={error} />
    </div>
  );
}

export interface ValidatedTextAreaProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  maxLength?: number;
  rows?: number;
  guard?: GuardMode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  variant?: Variant;
  /** Show a "120 / 500" counter — worth it on the fields with a real cap (notes, descriptions). */
  showCount?: boolean;
}

export function ValidatedTextArea({
  label,
  value,
  onChange,
  onBlur,
  error,
  required,
  maxLength,
  rows = 3,
  guard,
  placeholder,
  disabled,
  className,
  variant = 'float',
  showCount
}: ValidatedTextAreaProps) {
  const handle = (raw: string) => onChange(guard ? applyGuard(raw, guard, maxLength) : raw);
  return (
    <div className={className}>
      <div
        className={cn(
          'mb-1.5 flex items-center justify-between text-[11px] font-semibold text-content-muted',
          variant === 'stack' && 'uppercase tracking-wider'
        )}
      >
        <span>
          {label}
          {required && <span className='text-danger'> *</span>}
        </span>
        {showCount && maxLength != null && (
          <span className={cn(value.length >= maxLength && 'text-danger')}>
            {value.length} / {maxLength}
          </span>
        )}
      </div>
      <textarea
        value={value}
        onChange={(e) => handle(e.target.value)}
        onBlur={onBlur}
        rows={rows}
        maxLength={maxLength}
        placeholder={placeholder}
        disabled={disabled}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        className={cn(
          'w-full resize-y rounded-xl bg-surface-card focus:outline-none',
          variant === 'stack'
            ? 'border-[1.5px] px-4 py-3 text-sm font-medium'
            : 'border px-3.5 py-2.5 text-[13px] text-content-primary',
          error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary',
          disabled && 'opacity-60'
        )}
      />
      <FieldError error={error} />
    </div>
  );
}

/** A checkbox with a required-state error, for consent / acknowledgement boxes. */
export function ValidatedCheckbox({
  label,
  checked,
  onChange,
  error,
  required,
  disabled,
  className
}: {
  label: React.ReactNode;
  checked: boolean;
  onChange: (v: boolean) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className='flex cursor-pointer items-center gap-2 text-[13.5px] font-semibold'>
        <input
          type='checkbox'
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          className={cn('h-4 w-4 accent-[var(--color-primary)]', error && 'outline outline-1 outline-danger')}
        />
        {label}
        {required && <span className='text-danger'>*</span>}
      </label>
      <FieldError error={error} />
    </div>
  );
}

/** A radio group with a required-state error. */
export function ValidatedRadioGroup({
  label,
  value,
  onChange,
  options,
  error,
  required,
  className
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly (string | { value: string; label: string })[];
  error?: string;
  required?: boolean;
  className?: string;
}) {
  const opts = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  return (
    <div className={className}>
      <div className='mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-content-muted'>
        {label}
        {required && <span className='text-danger'> *</span>}
      </div>
      <div className='flex flex-wrap items-center gap-x-5 gap-y-2'>
        {opts.map((o) => (
          <label key={o.value} className='flex cursor-pointer items-center gap-2 text-[13.5px] font-semibold'>
            <input
              type='radio'
              checked={value === o.value}
              onChange={() => onChange(o.value)}
              className='h-4 w-4 accent-[var(--color-primary)]'
            />
            {o.label}
          </label>
        ))}
      </div>
      <FieldError error={error} />
    </div>
  );
}
